import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CamundaTask } from 'app/modules/admin/bpmn-forms/camunda-task.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, filter, map, switchMap, take, tap } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class TasksService {
	// Private

	apiUrl = environment.url;
	camundaURL = 'http://localhost:8080/engine-rest/';

	private readonly task: BehaviorSubject<CamundaTask | null> = new BehaviorSubject(null);
	private readonly tasks: BehaviorSubject<CamundaTask[] | null> = new BehaviorSubject(null);

	/**
	 * Constructor
	 */
	constructor(private readonly httpClient: HttpClient) {
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Acessor de Task
	 */
	get task$(): Observable<CamundaTask> {
		return this.task.asObservable();
	}

	/**
	 * Acessor de Tasks
	 */
	get tasks$(): Observable<CamundaTask[]> {
		return this.tasks.asObservable();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Get tasks
	 */
	getTasks(): Observable<CamundaTask[]> {
		let headers = new HttpHeaders();
		headers = headers.append('Content-Type', 'application/json ');
		headers = headers.append('Access-Control-Allow-Origin', '*');

		// api/task
		return this.httpClient.get<CamundaTask[]>(this.apiUrl + 'task', { headers: headers, params: { type: 'tasks' } }).pipe(
			tap((response) => {
				this.tasks.next(response);
			})
		);
	}

	/**
	 * Update tasks orders
	 *
	 * @param tasks
	 */
	updateTasksOrders(tasks: CamundaTask[]): Observable<CamundaTask[]> {
		return this.httpClient.patch<CamundaTask[]>('api/apps/tasks/order', { tasks });
	}

	/**
	 * Search tasks with given query
	 *
	 * @param query
	 */
	searchTasks(query: string): Observable<CamundaTask[] | null> {
		return this.httpClient.get<CamundaTask[] | null>('api/apps/tasks/search', { params: { query } });
	}

	/**
	 * Create task
	 *
	 * @param type
	 */
	createTask(type: string): Observable<CamundaTask> {
		return this.tasks$.pipe(
			take(1),
			switchMap(tasks => this.httpClient.post<CamundaTask>('api/apps/tasks/task', { type }).pipe(
				map((newTask) => {

					// Update the tasks with the new task
					this.tasks.next([newTask, ...tasks]);

					// Return the new task
					return newTask;
				})
			))
		);
	}

	/**
	 * Start Task
	 *
	 * @description start a new task
	 * @param {*} [data] data to start a new task
	 * @return {*}  {Observable<any>} return the new task
	 */
	startTask(data?: any): Observable<any> {
		return this.httpClient.post<any>(this.apiUrl + 'task', { data })
	}

	/**
	 * ### Completar Tarefa
	 * @description Completa uma tarefa	do camunda
	 * @param {string} taskID o ID da tarefa a ser concluída
	 * @param {{}} [action] ação a ser executada
	 * @return {*}  {Observable<any>} retorna a resposta da requisição
	 */
	completeTask(taskID: string, action?: {}): Observable<any> {
		return this.httpClient.post<any>(this.apiUrl + 'task/' + taskID + '/complete', { action })
			.pipe(
				tap((response) => {
				})
			)
	}

	/**
	 * Complete task
	 *
	 * @param taskid
	 */
	completeTaskOLD(taskid: string, action: string): Observable<any> {

		return this.httpClient.post<any>('http://localhost:8081/engine-rest/task/' + taskid + '/complete', {
			'variables': {
				'action': {
					'value': action
				}
			},
			'withVariablesInReturn': true
		}).pipe(
			map((newTask) => {

				// Update the tasks with the new task
				this.tasks.next([newTask]);

				// Return the new task
				return newTask;
			}));
	}

	/**
	 * Update task
	 *
	 * @param id
	 * @param task
	 */
	updateTask(id: string, task: CamundaTask): Observable<CamundaTask> {
		return this.tasks$
			.pipe(
				take(1),
				switchMap(tasks => this.httpClient.patch<CamundaTask>('api/apps/tasks/task', {
					id,
					task
				}).pipe(
					map((updatedTask) => {

						// Find the index of the updated task
						const index = tasks.findIndex(item => item.id === id);

						// Update the task
						tasks[index] = updatedTask;

						// Update the tasks
						this.tasks.next(tasks);

						// Return the updated task
						return updatedTask;
					}),
					switchMap(updatedTask => this.task$.pipe(
						take(1),
						filter(item => item && item.id === id),
						tap(() => {

							// Update the task if it's selected
							this.task.next(updatedTask);

							// Return the updated task
							return updatedTask;
						})
					))
				))
			);
	}

	/**
	 * Delete the task
	 *
	 * @param id
	 */
	deleteTask(id: string): Observable<boolean> {
		return this.tasks$.pipe(
			take(1),
			switchMap(tasks => this.httpClient.delete('api/apps/tasks/task', { params: { id } }).pipe(
				map((isDeleted: boolean) => {

					// Find the index of the deleted task
					const index = tasks.findIndex(item => item.id === id);

					// Delete the task
					tasks.splice(index, 1);

					// Update the tasks
					this.tasks.next(tasks);

					// Return the deleted status
					return isDeleted;
				})
			))
		);
	}
}
